import { Capacitor } from "@capacitor/core";
import { StatusBar } from "@capacitor/status-bar";
import { getPlatform, isNativePlatform } from "@lib/platform";

type SafeAreaSide = "top" | "right" | "bottom" | "left";

const ENVS: Record<SafeAreaSide, string> = {
  top: "safe-area-inset-top",
  right: "safe-area-inset-right",
  bottom: "safe-area-inset-bottom",
  left: "safe-area-inset-left",
};

const CSS_VAR: Record<SafeAreaSide, string> = {
  top: "--safe-area-top",
  right: "--safe-area-right",
  bottom: "--safe-area-bottom",
  left: "--safe-area-left",
};

function parsePx(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function measureEnvInset(side: SafeAreaSide): string {
  if (!document.body) return "0px";

  const el = document.createElement("div");
  el.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;inset:0;padding:0;";
  const env = ENVS[side];
  if (side === "top") el.style.paddingTop = `env(${env}, 0px)`;
  if (side === "right") el.style.paddingRight = `env(${env}, 0px)`;
  if (side === "bottom") el.style.paddingBottom = `env(${env}, 0px)`;
  if (side === "left") el.style.paddingLeft = `env(${env}, 0px)`;

  document.body.appendChild(el);
  const styles = getComputedStyle(el);
  const value =
    side === "top"
      ? styles.paddingTop
      : side === "right"
        ? styles.paddingRight
        : side === "bottom"
          ? styles.paddingBottom
          : styles.paddingLeft;
  el.remove();
  return value || "0px";
}

function readInset(side: SafeAreaSide): string {
  const root = document.documentElement;
  const computed = getComputedStyle(root);
  const injected = computed
    .getPropertyValue(`--safe-area-inset-${side}`)
    .trim();
  if (injected && parsePx(injected) > 0) {
    return injected;
  }
  return measureEnvInset(side);
}

function applyInsetVar(side: SafeAreaSide, value: string): void {
  const root = document.documentElement;
  const varName = CSS_VAR[side];
  // Never pin 0px inline — that overrides :root max(env(), …) until the next sync.
  if (parsePx(value) > 0) {
    root.style.setProperty(varName, value);
  } else {
    root.style.removeProperty(varName);
  }
}

function markSafeAreaReady(): void {
  const root = document.documentElement;
  const top = readInset("top");
  const bottom = readInset("bottom");
  if (parsePx(top) <= 0 && parsePx(bottom) <= 0) return;
  if (root.classList.contains("safe-area-ready")) return;

  root.classList.add("safe-area-ready");
  window.dispatchEvent(new Event("resize"));
}

/** Writes resolved insets to --safe-area-* when non-zero (native WebView cold start). */
export function syncSafeAreaCssVars(): void {
  if (typeof window === "undefined") return;

  applyInsetVar("top", readInset("top"));
  applyInsetVar("right", readInset("right"));
  applyInsetVar("bottom", readInset("bottom"));
  applyInsetVar("left", readInset("left"));
  markSafeAreaReady();
}

function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/** iOS WebView often reports 0 insets until after splash + status bar settle. */
export async function syncSafeAreaAfterNativeLaunch(): Promise<void> {
  if (!isNativePlatform()) return;

  await waitForNextFrame();
  syncSafeAreaCssVars();

  const delays = [0, 50, 100, 200, 400, 800];
  for (const delay of delays) {
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    syncSafeAreaCssVars();
    const top = parsePx(readInset("top"));
    const bottom = parsePx(readInset("bottom"));
    if (top > 0 && bottom > 0) break;
  }

  // Never leave the shell hidden if env() is slow on first launch.
  if (!document.documentElement.classList.contains("safe-area-ready")) {
    document.documentElement.classList.add("safe-area-ready");
    window.dispatchEvent(new Event("resize"));
  }
}

export function initSafeAreaEnvironment(): void {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  html.classList.add("viewport-fit-cover");

  if (!isNativePlatform()) return;

  html.classList.add("native-app");
  html.dataset.platform = getPlatform();
}

export async function configureNativeSafeArea(): Promise<void> {
  if (!isNativePlatform()) return;

  if (Capacitor.isPluginAvailable("StatusBar")) {
    try {
      await StatusBar.setOverlaysWebView({ overlay: true });
    } catch {
      // ignore
    }
  }
}

export function bindSafeAreaResizeSync(): () => void {
  if (typeof window === "undefined") return () => {};

  const sync = () => syncSafeAreaCssVars();
  window.addEventListener("resize", sync);
  window.addEventListener("orientationchange", sync);
  window.visualViewport?.addEventListener("resize", sync);

  return () => {
    window.removeEventListener("resize", sync);
    window.removeEventListener("orientationchange", sync);
    window.visualViewport?.removeEventListener("resize", sync);
  };
}
